/**
 * feat-270 AT-27d — the select-all confirm. `design/approval-queue.html` state K,
 * `design/NOTES.md` §6b.
 *
 * **The shell is `PublishGuardDialog.tsx:65-129` verbatim** — same overlay, same
 * `max-w-md` panel, same footer pair, same dismissal routes. Two BEHAVIOURAL
 * contracts come with it and both transfer, which is the reason the shell is
 * copied rather than reinvented:
 *
 *  1. **Initial focus lands on Cancel**, not on the confirm. Its stated reason —
 *     *"a stray Enter should not publish incomplete content to learners"* — is
 *     this dialog's entire premise, one level up.
 *  2. **The confirm is `coral`, not `danger`.** Approving is the go-live action,
 *     not a destructive one, and `danger-*` is reserved for the irreversible.
 *     There IS no un-approve (spec D8), but the recovery — revert from History —
 *     exists, so this is reversible in the sense that matters.
 *
 * Escape, an overlay mousedown, and Cancel all dismiss to the same state, and
 * dismissing approves NOTHING.
 *
 * A real dialog rather than `window.confirm` is forced by the codebase's own
 * split, recorded in `PublishGuardDialog`'s header: `window.confirm` for a single
 * yes/no on an existing thing, **a real dialog when a list must be shown.** Here
 * a list must be shown.
 *
 * ⚠ **ITS JOB IS NOT TO ASK "ARE YOU SURE".** A bare count adds friction without
 * information, which is this feature's stated failure mode — *"just another layer
 * of a lot of work"* — merely relocated. Its job is to name what a select-all
 * selection HIDES: the reader chose every waiting lesson with one click, so they
 * have not read those diffs. Two subsets earn a sentence each because they are
 * exactly what a scroll-and-click misses — never-approved lessons (whole lessons
 * reaching employees for the first time, absent from the reader's "approve my
 * recent changes" mental model) and answer-key changes (AT-13's rogue vector, a
 * one-character diff that looks like nothing).
 *
 * Both counts come from data already on screen. No new endpoint, no new field.
 */

import React, { useEffect, useRef } from 'react';
import { APPROVAL_QUEUE_COPY as C, itemCount, changeCount } from '../approvalCopy';
import type { SelectionSummary } from '../approvalSelection';

export interface ApproveAllDialogProps {
  summary: SelectionSummary;
  onCancel: () => void;
  onConfirm: () => void;
}

export const ApproveAllDialog: React.FC<ApproveAllDialogProps> = ({
  summary,
  onCancel,
  onConfirm,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Contract 1. Initial focus lands on Cancel, NOT on the confirm: a stray
    // Enter must not publish to every employee at once.
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const neverApproved = summary.neverApproved;
  const answerKey = summary.answerKeyLessons;
  const changed = summary.lessons.filter((l) => l.state !== 'never_approved');
  const answerKeyIds = new Set(answerKey.map((l) => l.lesson_id));

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      data-testid="approve-all-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="approve-all-title"
        className="bg-white rounded-2xl border border-softBorder shadow-xl w-full max-w-md p-6"
      >
        <h3 id="approve-all-title" className="font-display text-lg font-semibold break-words">
          {C.confirmTitle(summary.lessonCount)}
        </h3>
        <p className="text-sm text-inkMuted mt-1.5 leading-relaxed">
          {C.confirmLedePrefix}
          <strong className="text-ink">{C.confirmLedeStrong}</strong>
          {C.confirmLede(summary.changeCount)}
        </p>

        {/* The two subsets a select-all hides. `attention-*`, because this is a
            thing the reader can act on from this dialog (the feat-259 split). */}
        {(neverApproved.length > 0 || answerKey.length > 0 || summary.answerKeyCountIsFloor) && (
          <div className="mt-3 rounded-xl bg-attention-surface border border-attention-line/50 text-attention-on px-3 py-2.5 text-[11.5px] leading-snug">
            {neverApproved.length > 0 && (
              <div>
                <strong className="font-bold">
                  {C.confirmNeverApproved(neverApproved.length)}
                </strong>
                {C.confirmNeverApprovedRest(neverApproved.length)}
              </div>
            )}
            {/*
              AT-27e — FOUR cases, and the fourth is the one that matters.
              `items` truncates at `item_limit` (ADR-B §3.2, default 10), so a
              count over the RENDERED items is a lower bound:

                not truncated, n > 0  → the exact count, no hedge
                not truncated, n = 0  → NO LINE. Correct: we looked, there are none.
                truncated,     n > 0  → "At least N …"
                truncated,     n = 0  → the MAYBE line, below.

              The fourth case is why this is driven off `items_truncated` and not
              off the count. "At least N" only helps when N > 0; if the only
              answer-key change in the selection sits past the truncation point,
              the count is zero, no line renders, and the one dialog written to
              name the rogue vector goes silent — which a reader takes as "there
              are none". Absence of a marker is a claim.

              This is ordinary, not exotic: a fresh tenant's first approval makes
              every item "created", so most lessons truncate.
            */}
            {answerKey.length > 0 ? (
              <div
                data-testid="confirm-answer-keys"
                className={neverApproved.length > 0 ? 'mt-1' : undefined}
              >
                <strong className="font-bold">
                  {summary.answerKeyCountIsFloor ? 'At least ' : ''}
                  {C.confirmAnswerKeys(answerKey.length)}
                </strong>
                {C.confirmAnswerKeysRest}
              </div>
            ) : summary.answerKeyCountIsFloor ? (
              <div
                data-testid="confirm-answer-keys"
                className={neverApproved.length > 0 ? 'mt-1' : undefined}
              >
                <strong className="font-bold">{C.confirmAnswerKeysMaybe}</strong>
                {C.confirmAnswerKeysMaybeRest}
              </div>
            ) : null}
          </div>
        )}

        <div className="mt-3 border border-softBorder rounded-xl overflow-hidden">
          {/* Scrolls; it NEVER truncates to "+4 more". The count in the lede is
              the number the reader is deciding on, and a list that disagrees with
              its own count is worse than no list. */}
          <div className="max-h-[13rem] overflow-y-auto divide-y divide-[rgba(0,0,0,0.06)]">
            {neverApproved.length > 0 && (
              <>
                <div className="px-3 py-1.5 bg-cream text-[11px] font-bold uppercase tracking-wide text-inkMuted">
                  {C.confirmGroupNeverApproved}
                </div>
                {neverApproved.map((l) => (
                  <div
                    key={l.lesson_id}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm"
                  >
                    <span className="text-[10px] font-bold rounded-md px-1.5 py-0.5 shrink-0 bg-attention-surface text-attention-on">
                      {C.confirmNewBadge}
                    </span>
                    <span className="font-semibold min-w-0 truncate">{l.title}</span>
                    <span className="text-xs text-inkMuted ml-auto shrink-0">
                      {itemCount(l.pending_item_count)}
                    </span>
                  </div>
                ))}
              </>
            )}
            {changed.length > 0 && (
              <>
                <div className="px-3 py-1.5 bg-cream text-[11px] font-bold uppercase tracking-wide text-inkMuted">
                  {C.confirmGroupChanged}
                </div>
                {changed.map((l) => (
                  <div
                    key={l.lesson_id}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm"
                  >
                    <span className="font-semibold min-w-0 truncate">{l.title}</span>
                    {answerKeyIds.has(l.lesson_id) && (
                      <span className="text-[10px] font-bold rounded px-1.5 py-0.5 shrink-0 bg-cream-dark text-inkMuted">
                        {C.answerKeyTag}
                      </span>
                    )}
                    <span className="text-xs text-inkMuted ml-auto shrink-0">
                      {changeCount(l.pending_item_count)}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="text-inkMuted text-sm font-semibold px-3 py-2 rounded-full hover:bg-gray-100"
          >
            {C.confirmCancel}
          </button>
          {/* Contract 2. `coral`, not `danger`. */}
          <button
            type="button"
            onClick={onConfirm}
            className="bg-coral text-white font-display font-semibold rounded-full px-5 py-2 text-sm hover:brightness-95 shrink-0"
          >
            {C.confirmApprove(summary.lessonCount)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApproveAllDialog;
